import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Redirect, useHistory } from "react-router-dom";
import axios from "axios";
import {
  FaBookOpen,
  FaCamera,
  FaFilter,
  FaHome,
  FaInfoCircle,
  FaPencilAlt,
  FaPlus,
  FaSearch,
  FaSyncAlt,
  FaTrash,
} from "react-icons/fa";
import BackButton from "../ChildComponent/BackButton";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

//Created By Cursor
const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const BRAND = {
  navy: "#0f172a",
  teal: "#0288d1",
  bg: "#f5f7fb",
};

const DIFFICULTY_STYLES = {
  Easy: { bg: "#E6F8F6", fg: "#0F8A7E" },
  Medium: { bg: "#FFF3E0", fg: "#B5750B" },
  Hard: { bg: "#FCE8E8", fg: "#C23B3B" },
};

const TYPE_LABELS = {
  mcq: "MCQ",
  short: "Short Answer",
  long: "Long Answer",
  fill_blank: "Fill in the Blank",
  match: "Match the Following",
  true_false: "True / False",
};

//Created By Cursor
function subjectsForClass(classId, rows = []) {
  const seen = new Map();
  rows
    .filter(
      (r) =>
        String(r.class_id) === String(classId) &&
        String(r.status || "Active").toLowerCase() !== "inactive"
    )
    .forEach((r) => {
      if (!seen.has(String(r.subject_id))) seen.set(String(r.subject_id), r);
    });
  return Array.from(seen.values());
}

//Created By Cursor
function subjectLabel(row) {
  return row?.subject_name || row?.book_name || row?.subject_id || "Subject";
}

//Created By Cursor
function Pill({ children, bg, fg }) {
  return (
    <span className="qb-pill" style={{ backgroundColor: bg, color: fg }}>
      {children}
    </span>
  );
}

export default function QuestionBank() {
  const history = useHistory();
  const loggedIn = localStorage.getItem("token") != null;
  const reqIdRef = useRef(0);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [questions, setQuestions] = useState([]);
  const [classes, setClasses] = useState([]);
  const [classSubjects, setClassSubjects] = useState([]);
  const [search, setSearch] = useState("");
  const [classFilter, setClassFilter] = useState("all");
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [difficultyFilter, setDifficultyFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const [chapterFilter, setChapterFilter] = useState("");

  //Created By Cursor
  useEffect(() => {
    window.scrollTo(0, 0);
    const current = SessionManager.getCurrentSession()?.[0];
    setSessionId(current?.session_id || "");
  }, []);

  //Created By Cursor
  const loadLookups = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      myToast.notify_warning("School code is missing.");
      return;
    }
    try {
      const [classRes, subjectRes] = await Promise.all([
        axios.get(`${API_BASE()}getQuestionBankClasses.php`, {
          params: { school_code: schoolCode, session_id: sessionId || undefined },
        }),
        axios.get(`${API_BASE()}getQuestionBankClassSubjects.php`, {
          params: { school_code: schoolCode, session_id: sessionId || undefined },
        }),
      ]);
      setClasses(
        classRes.data?.api_status === "OK" && Array.isArray(classRes.data.data)
          ? classRes.data.data
          : []
      );
      setClassSubjects(
        subjectRes.data?.api_status === "OK" &&
          Array.isArray(subjectRes.data.data)
          ? subjectRes.data.data
          : []
      );
    } catch (err) {
      console.error("[QuestionBank] lookup error:", err);
      myToast.notify_warning("Failed to load classes/subjects.");
      setClasses([]);
      setClassSubjects([]);
    }
  }, [sessionId]);

  //Created By Cursor
  const loadQuestions = useCallback(
    async ({ soft = false } = {}) => {
      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) {
        setLoading(false);
        return;
      }
      const reqId = ++reqIdRef.current;
      if (soft) setRefreshing(true);
      else setLoading(true);

      try {
        const params = {
          school_code: schoolCode,
          session_id: sessionId || undefined,
        };
        if (classFilter !== "all") params.class_id = classFilter;
        if (subjectFilter !== "all") params.subject_id = subjectFilter;
        if (difficultyFilter !== "All") params.difficulty = difficultyFilter;
        if (typeFilter !== "All") params.type = typeFilter;
        if (search.trim()) params.search = search.trim();
        if (chapterFilter.trim()) params.chapter = chapterFilter.trim();

        const res = await axios.get(`${API_BASE()}getQuestionBank.php`, {
          params,
        });
        if (reqId !== reqIdRef.current) return;
        if (res.data?.api_status === "OK" && Array.isArray(res.data.data)) {
          setQuestions(res.data.data);
        } else {
          setQuestions([]);
        }
      } catch (err) {
        if (reqId !== reqIdRef.current) return;
        console.error("[QuestionBank] list error:", err);
        setQuestions([]);
        myToast.notify_warning("Failed to load questions.");
      } finally {
        if (reqId === reqIdRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [
      sessionId,
      classFilter,
      subjectFilter,
      difficultyFilter,
      typeFilter,
      search,
      chapterFilter,
    ]
  );

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    const t = setTimeout(() => {
      loadQuestions();
    }, 250);
    return () => clearTimeout(t);
  }, [loadQuestions]);

  const classNameById = useMemo(() => {
    const map = new Map();
    classes.forEach((c) => map.set(String(c.class_id), c.class_name));
    return map;
  }, [classes]);

  const subjectNameById = useMemo(() => {
    const map = new Map();
    classSubjects.forEach((s) =>
      map.set(String(s.subject_id), subjectLabel(s))
    );
    return map;
  }, [classSubjects]);

  const subjectOptions = useMemo(() => {
    if (classFilter !== "all") {
      return subjectsForClass(classFilter, classSubjects);
    }
    const seen = new Map();
    classSubjects.forEach((r) => {
      if (
        String(r.status || "Active").toLowerCase() !== "inactive" &&
        !seen.has(String(r.subject_id))
      ) {
        seen.set(String(r.subject_id), r);
      }
    });
    return Array.from(seen.values());
  }, [classSubjects, classFilter]);

  useEffect(() => {
    if (subjectFilter === "all") return;
    if (
      !subjectOptions.some(
        (s) => String(s.subject_id) === String(subjectFilter)
      )
    ) {
      setSubjectFilter("all");
    }
  }, [subjectOptions, subjectFilter]);

  //Created By Cursor
  const handleDelete = useCallback(
    async (id) => {
      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode || !id) return;
      if (
        !window.confirm(
          "Delete this question? If it is used in a paper it will be archived; otherwise it will be removed permanently."
        )
      ) {
        return;
      }
      try {
        const res = await axios.post(`${API_BASE()}deleteQuestionBank.php`, {
          school_code: schoolCode,
          id,
        });
        if (res.data?.api_status === "OK") {
          myToast.notify_success(
            res.data?.message ||
              (res.data?.delete_mode === "hard"
                ? "Question deleted permanently."
                : "Question archived.")
          );
          await loadQuestions({ soft: true });
        } else {
          myToast.notify_warning(
            res.data?.api_remark || "Failed to delete question."
          );
        }
      } catch (err) {
        console.error("[QuestionBank] delete error:", err);
        myToast.notify_warning("Failed to delete question.");
      }
    },
    [loadQuestions]
  );

  if (!loggedIn) return <Redirect to="/Login" />;

  const busy = loading || refreshing;
  const subjectCount = new Set(questions.map((q) => q.subject_id)).size;

  return (
    <div
      className="content payroll-page sm-page qb-page"
      style={{ background: BRAND.bg }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade6 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <span style={{ color: COLORS.white }}>Question Bank</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaBookOpen />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Question Bank</h1>
              <p className="sm-hero__sub">
                {questions.length} questions · {subjectCount} subjects
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              title="Refresh"
              onClick={() => loadQuestions({ soft: true })}
              disabled={busy}
            >
              <FaSyncAlt aria-hidden />
            </button>
            <button
              type="button"
              className="qb-hero-btn"
              onClick={() => history.push("/AdminMain/AddQuestionBank")}
              disabled={!classes.length}
            >
              <FaPlus /> Add question
            </button>
          </div>
        </header>

        <div className="qb-content">
          <div className="qb-filters">
            <label className="qb-search">
              <FaSearch aria-hidden />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search questions..."
                aria-label="Search questions"
              />
            </label>
            <div className="qb-filter-row">
              <FaFilter className="qb-filter-icon" aria-hidden />
              <select
                value={classFilter}
                onChange={(e) => setClassFilter(e.target.value)}
                aria-label="Class filter"
              >
                <option value="all">All Classes</option>
                {classes.map((c) => (
                  <option key={c.class_id} value={c.class_id}>
                    {c.class_name}
                  </option>
                ))}
              </select>
              <select
                value={subjectFilter}
                onChange={(e) => setSubjectFilter(e.target.value)}
                aria-label="Subject filter"
              >
                <option value="all">All Subjects</option>
                {subjectOptions.map((s) => (
                  <option key={s.subject_id} value={s.subject_id}>
                    {subjectLabel(s)}
                  </option>
                ))}
              </select>
              <input
                type="search"
                value={chapterFilter}
                onChange={(e) => setChapterFilter(e.target.value)}
                placeholder="Chapter / topic"
                aria-label="Chapter filter"
                className="qb-chapter-input"
              />
              <select
                value={difficultyFilter}
                onChange={(e) => setDifficultyFilter(e.target.value)}
                aria-label="Difficulty filter"
              >
                {["All", "Easy", "Medium", "Hard"].map((d) => (
                  <option key={d} value={d}>
                    {d === "All" ? "All Difficulty" : d}
                  </option>
                ))}
              </select>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                aria-label="Type filter"
              >
                <option value="All">All Types</option>
                {Object.entries(TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {loading && !questions.length ? (
            <div className="qb-empty">
              <MyLoader />
            </div>
          ) : questions.length === 0 ? (
            <div className="qb-empty">
              {classes.length
                ? "No questions match these filters."
                : "No classes found for this session."}
            </div>
          ) : (
            <div className="qb-list">
              {questions.map((q) => {
                const diff =
                  DIFFICULTY_STYLES[q.difficulty] || DIFFICULTY_STYLES.Easy;
                return (
                  <article key={q.id} className="qb-card">
                    <div className="qb-card__body">
                      <p className="qb-card__text">{q.question_text}</p>
                      <div className="qb-card__meta">
                        <Pill bg="#EEF1F8" fg={BRAND.navy}>
                          {classNameById.get(String(q.class_id)) || "Class"}
                        </Pill>
                        <Pill bg="#EEF1F8" fg={BRAND.navy}>
                          {subjectNameById.get(String(q.subject_id)) ||
                            "Subject"}
                        </Pill>
                        {q.chapter ? (
                          <Pill bg="#FFF6E9" fg="#B5750B">
                            {q.chapter}
                          </Pill>
                        ) : null}
                        <Pill bg={diff.bg} fg={diff.fg}>
                          {q.difficulty}
                        </Pill>
                        <Pill bg="#EAF9F7" fg="#0F8A7E">
                          {TYPE_LABELS[q.type] || q.type}
                        </Pill>
                        <Pill bg="#F2F2F2" fg="#666">
                          {q.marks} {Number(q.marks) === 1 ? "mark" : "marks"}
                        </Pill>
                        {q.source_type === "scanned" ? (
                          <span className="qb-scanned">
                            <FaCamera /> Scanned
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className="qb-card__actions">
                      <button
                        type="button"
                        className="qb-details-btn"
                        title="View details"
                        onClick={() =>
                          history.push(`/AdminMain/AddQuestionBank/${q.id}`)
                        }
                      >
                        <FaInfoCircle aria-hidden />
                        <span>Details</span>
                      </button>
                      <button
                        type="button"
                        className="qb-icon-btn"
                        title="Edit"
                        onClick={() =>
                          history.push(`/AdminMain/AddQuestionBank/${q.id}`)
                        }
                      >
                        <FaPencilAlt />
                      </button>
                      <button
                        type="button"
                        className="qb-icon-btn qb-icon-btn--danger"
                        title="Delete"
                        onClick={() => handleDelete(q.id)}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {busy && questions.length > 0 ? <MyLoader /> : null}
    </div>
  );
}
